import { type ReactElement } from 'react';
import RankedList, { type RankedListColumn, type RankedListPrimary } from '@/design/RankedList/RankedList';
import { formatCount, formatPercent, formatTokens, formatUsd } from '@/shared/analytics/MetricFormatting';
import type { UseCaseBreakdownRow } from './UseCaseBreakdownViewModel';

const PEOPLE_WIDTH = 54;
const PROMPTS_WIDTH = 60;
const TOKENS_WIDTH = 64;
const COST_WIDTH = 68;

interface UseCaseRankedListProps {
    rows: UseCaseBreakdownRow[];
    /** Column header for the value column, e.g. `Category`. */
    lensHeader: string;
    /** Plural noun for the pagination caption, e.g. `task categories`. */
    lensPlural: string;
    /** Render each value's usage-category taxonomy root as a dimmed subline. */
    showParents: boolean;
    /** Row link target; rows render as real links when set. Receives the full row so callers can branch on nesting. */
    hrefFor?: (row: UseCaseBreakdownRow) => string;
    /** Click handler alternative to `hrefFor` for in-place actions (e.g. switching tabs). */
    onRowClick?: (row: UseCaseBreakdownRow) => void;
}

/**
 * The Use Cases ranked list: classifier values ranked by prompt volume, with the
 * taxonomy parent as a dimmed subline. A thin column adapter over the shared
 * {@link RankedList}; rows link to the category page (`hrefFor`) or fire
 * `onRowClick` when navigation is in-place.
 */
export default function UseCaseRankedList({ rows, lensHeader, lensPlural, showParents, hrefFor, onRowClick }: UseCaseRankedListProps): ReactElement {
    const primary: RankedListPrimary<UseCaseBreakdownRow> = {
        key: 'value',
        header: lensHeader,
        label: (row) => (row.value === '—' ? 'Unclassified' : row.value),
        subline: showParents ? (row): string | undefined => (row.parentValue === null ? (row.parent ?? '—') : undefined) : undefined,
        fill: (row) => row.widthPct,
        share: (row) => formatPercent(row.share),
        sort: { get: (row) => row.value, initialDir: 'asc' },
    };

    const columns: RankedListColumn<UseCaseBreakdownRow>[] = [
        { key: 'users', header: 'Users', width: PEOPLE_WIDTH, render: (row) => formatCount(row.users), sort: { get: (row) => row.users, initialDir: 'desc' }, visibleFrom: 'sm', size: 'xs' },
        { key: 'turns', header: 'Prompts', width: PROMPTS_WIDTH, render: (row) => formatCount(row.turns), sort: { get: (row) => row.turns, initialDir: 'desc' } },
        { key: 'totalTokens', header: 'Tokens', width: TOKENS_WIDTH, render: (row) => formatTokens(row.totalTokens), sort: { get: (row) => row.totalTokens, initialDir: 'desc' } },
        { key: 'allocatedCostUsd', header: 'Cost', width: COST_WIDTH, render: (row) => formatUsd(row.allocatedCostUsd), sort: { get: (row) => row.allocatedCostUsd, initialDir: 'desc' } },
    ];

    return (
        <RankedList
            rows={rows}
            primary={primary}
            columns={columns}
            rowKey={(row) => (row.parentValue === null ? row.value : `${row.parentValue}/${row.value}`)}
            accent="teal"
            initialSort={{ key: 'totalTokens', dir: 'desc' }}
            hrefFor={hrefFor}
            onRowClick={onRowClick}
            unitPlural={lensPlural}
            childrenFor={(row) => row.children}
            accentFor={(_row, depth) => (depth === 0 ? 'teal' : 'cyan')}
        />
    );
}
